import { FC, useRef } from 'react';
import cls from './Table.module.scss';
import { ReactComponent as ActionIcon } from '@icons/sort.svg';
import { useObserver } from '@src/hooks/useObserver';
import { Box, Stack, Typography, styled } from '@mui/material';
import { theme } from '@src/theme';
import { border, buildDataTestId, buildDataTestIdAttribute, hexOpacity, scrollBarMD,  } from '@src/utils';

type TableActionType = {
  action?: () => void;
  component?: {
    component: FC<any>;
    props?: any;
  };
  position?: 'start' | 'end';
};

interface ActionProps {
  action: TableActionType;
}
export interface ITableHeaderItem {
  name: string;
  action?: TableActionType;
}

interface ITableProps<T extends { id?: string | number }> {
  scrollRest?: () => void;
  /** Состояние подгруздки данных (ожидание/обновление данных) */
  loading: boolean;
  /** Массив данных для таблицы */
  data: T[];
  /** Компонент строки принимает в пропсы данные из массива */
  rowComponent: RowComponentType<T>;
  /** Название колонок таблицы */
  headerList: ITableHeaderItem[];
  /** разметка таблицы display:grid */
  gridTemplateColumns: string;
  /** класс для обертки таблицы */
  className?: {
    wrapper?: string;
    header?: string;
    body?: string;
    row?: string;
  };
  keyIndicator?: keyof T;
  /** разница высоты таблицы */
  viewHeightTable?: number;
  /** Компонент при пустых значениях */
  emptyData?: JSX.Element;
  showAlert?: boolean;
  minHeight?: number;
  maxHeight?: number;
  positionLoader?: 'static' | 'relative' | 'absolute' | 'sticky' | 'fixed';
  overlayLoader?: 'light' | 'dark' | 'white' | undefined;
  dataTestId?: string;
}

const Action: FC<ActionProps> = ({ action }) => {
  const { action: fn, component } = action;
  if (component) {
    const { component: Component, props } = component;
    return <Component {...props} />;
  }
  return <ActionIcon onClick={fn} className={cls.sort} />;
};

export interface ITableRowProps<T> {
  data: T;
  className: string;
  gridTemplateColumns?: string;
  totalData?: T[];
  index?: number;
  dataTestId?: string;
}

export type TableRowComponentType<T> = FC<ITableRowProps<T>>;

type RowComponentType<T> = {
  component: TableRowComponentType<T>;
  props?: Record<string, unknown>;
};

type BodyType<T extends { id?: string | number }> = {
  data: T[];
  rowComponent: RowComponentType<T>;
  gridTemplateColumns: string;
  keyIndicator: keyof T;
  className?: string;
  scrollRest?: () => void;
  loading: boolean;
  dataTestId?: string;
};

export const Body = <T extends { id?: string | number }>({
  data,
  rowComponent,
  gridTemplateColumns,
  keyIndicator,
  scrollRest,
  loading,
  dataTestId,
}: BodyType<T>): JSX.Element => {
  const reference = useRef<HTMLDivElement | null>(null);

  const { component: Component, props } = rowComponent;

  useObserver(reference, scrollRest, loading);

  return (
    <>
      <Stack gap="3px">
        {data.map((object, index) => (
          <Component
            data={object}
            className={`${cls.row}`}
            gridTemplateColumns={gridTemplateColumns}
            key={`${object[keyIndicator as string]}/table-component${index}`}
            totalData={data}
            index={index}
            dataTestId={buildDataTestId(dataTestId, `row-${index}`)}
            {...props}
          />
        ))}
      </Stack>
      <div ref={reference} />
    </>
  );
};

export const EmptySearch: React.FC<{ text: string }> = ({ text }) => {
  return (
    <Stack padding="36px" bgcolor="#fff" direction="row" border={border} mt="3px">
      <Typography fontSize="14px" fontWeight={500} color="grey.500" lineHeight="20px">
        {text}
      </Typography>
    </Stack>
  );
};

const LoadingLine = styled(Box)(
  ({ loading }: { loading: number }) => `
  height: 3px;
  position: relative;
  overflow: hidden;
  background-color: ${hexOpacity(theme.palette.secondary.main, '28%')};
  &:before {
    content: '';
    position: absolute;
    left: -50%;
    top: 0;
    width: 40%;
    height: 100%;
    background-color: ${hexOpacity(theme.palette.secondary.main, '63%')};
    animation: ${loading ? 'lineAnim 1s linear infinite' : 'none'};
  };
  @keyframes lineAnim {
    0% {
        left: -40%;
    }
    50% {
        left: 20%;
        width: 80%;
    }
    100% {
        left: 100%;
        width: 100%;
    }
  }
`,
);

const classes = {
  wrapper: {
    // borderTop: '3px solid #bfe5eb',
    paddingBottom: '10px',
    fontSize: '14px',
    flexGrow: 1,
    overflow: 'hidden',
  },
  header: {
    display: 'grid',
    padding: '14px 36px',
    color: theme.palette.grey['700'],
    backgroundColor: '#fff',
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '18px',
    border,
    marginBottom: '3px',
    '& > div': {
      display: 'flex',
      alignItems: 'center',

      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
};

export const Table = <T extends { id?: string | number }>({
  headerList,
  gridTemplateColumns,
  data,
  loading,
  rowComponent,
  scrollRest,
  keyIndicator = 'id',
  showAlert = true,
  emptyData,
  dataTestId,
}: ITableProps<T>) => {
  return (
    <Stack /* gap="3px" */ sx={classes.wrapper} {...buildDataTestIdAttribute(dataTestId)}>
      <LoadingLine loading={+loading} />
      <Box sx={{ ...classes.header, gridTemplateColumns }} {...buildDataTestIdAttribute(dataTestId, 'table-head')}>
        {headerList.map(({ name, action }, index) => {
          const nameDataTestId = name.split(' ').join('-').toLowerCase();
          const position = action?.position || 'end';
          return action?.component ? (
            <Action
              action={action}
              key={`${name}/table-header/${index}`}
              {...buildDataTestIdAttribute(dataTestId, `table-head-${nameDataTestId}`)}
            />
          ) : (
            <div
              key={`${name}/table-header/${index}`}
              {...buildDataTestIdAttribute(dataTestId, `table-head-${nameDataTestId}`)}
            >
              {action && position === 'start' && <Action action={action} />}
              <div>{name}</div>
              {action && position === 'end' && <Action action={action} />}
            </div>
          );
        })}
      </Box>

      <Box sx={{ ...scrollBarMD }} overflow="overlay">
        <div>
          {data.length > 0 && (
            <Body
              data={data}
              rowComponent={rowComponent}
              gridTemplateColumns={gridTemplateColumns}
              keyIndicator={keyIndicator}
              loading={loading}
              scrollRest={scrollRest}
              dataTestId={dataTestId}
            />
          )}
          {data.length === 0 && !loading && showAlert
            ? emptyData || (
                <EmptySearch text="There are no results matching your search. Try to change your search options." />
              )
            : null}
        </div>
      </Box>
    </Stack>
  );
};
